import { kebabCase } from "@portal/app/_common/lib/changeCase";
import { FALLBACK_ZIP } from "@portal/app/_common/lib/requestUtils";
import type { VocationIndustry } from "@portal/app/_common/types/vocationIndustry";

/**
 * The industries that have a dedicated `/{kebab-industry}/{zip}` DreamSearch
 * route.
 *
 * Deliberately not all of `VOCATION_INDUSTRY`: the other four (agriculture,
 * marketing and distribution, occupation and economics, other) exist only as
 * `?industry=` variants of `/explore`, which have no route directory of their
 * own. `dreamSearchRoutes.test.ts` asserts every entry here has a real
 * `app/{kebab}/[zip]/page.tsx`.
 */
export const DREAM_SEARCH_INDUSTRY_ROUTES = [
  "BUSINESS_AND_OFFICE",
  "HEALTH",
  "TECHNICAL_AND_COMMUNICATIONS",
  "TRADE_AND_INDUSTRY",
] as const satisfies readonly VocationIndustry[];

export type DreamSearchIndustryRoute = (typeof DREAM_SEARCH_INDUSTRY_ROUTES)[number];

/**
 * Marks a bare-route redirect that had no user, cookie, or Cloudflare location
 * and therefore had to use the synthetic fallback ZIP. This is UI provenance,
 * not a result-set filter, so metadata omits it from the page's og:url identity.
 */
export const DREAM_SEARCH_LOCATION_PROMPT_PARAM = "prompt_location";

/**
 * Distinguishes the hardcoded fallback from a real request for the same ZIP.
 * The value alone is not enough: 75001 is both a valid user location and the
 * sentinel returned when neither a cookie nor Cloudflare geo is available.
 */
export const isSyntheticDreamSearchFallback = ({
  requestedZip,
  geoCenter,
}: {
  requestedZip?: string | null;
  geoCenter: { zip?: string | null; city?: string | null; state?: string | null };
}): boolean =>
  !requestedZip && geoCenter.zip === FALLBACK_ZIP && !geoCenter.city && !geoCenter.state;

/**
 * The path form of an industry-scoped DreamSearch page, e.g. `/health/60601`.
 *
 * Deliberately not `urls.DreamSearch`, which emits the query-param form
 * (`/explore/60601?industry=health`). Both render the same page, so both
 * collapse onto this one shape as their `og:url` identity.
 */
export const getDreamSearchIndustryPath = (industry: VocationIndustry, zip: string): string =>
  `/${kebabCase(industry)}/${zip}`;

/**
 * The bare route path for a DreamSearch page — `/health` for an industry-scoped
 * route, `/explore` for the all-industries one.
 *
 * Derived rather than passed alongside `industry`, so the two cannot disagree.
 * The five routes share one `generateMetadata`, and a mismatch between the two
 * would emit a plausible-looking `<title>` and `og:url` for the wrong route.
 */
export const getDreamSearchBasePath = (industry?: DreamSearchIndustryRoute): string =>
  industry ? `/${kebabCase(industry)}` : "/explore";

/**
 * Resolves an `?industry=` query value to the routed industry it names, or
 * `undefined` if it names none.
 *
 * Deliberately strict, because the caller uses this to decide whether
 * `/explore/{zip}?industry=health` shares an `og:url` identity with
 * `/health/{zip}`:
 *
 * - Only the exact kebab-case spelling is accepted — the shape `urls.DreamSearch`
 *   emits and the only one that round-trips to a route path. Loose
 *   normalization is what makes this dangerous rather than merely permissive:
 *   the runtime filter parser truncates a value at the first non-`[a-zA-Z0-9-_]`
 *   character (`cleanURIComponentString`), so `?industry=trade%20and%20industry`
 *   reaches the query as `trade`, fails `assertIndustry`, and renders *all*
 *   industries — while a `constantCase` comparison here would have happily
 *   resolved it to TRADE_AND_INDUSTRY and merged that URL onto the identity of a
 *   result set the page never rendered.
 * - An array value (`?industry=health&industry=trade-and-industry`) resolves to
 *   `undefined`. It is a real multi-industry filter — `industry` maps to
 *   `program_vocation_industry` in `queryParamFiltersForDreamSearch` — and no
 *   single route represents that combination, so it must not be treated as a
 *   route restatement.
 * - A value outside `DREAM_SEARCH_INDUSTRY_ROUTES` resolves to `undefined`. The
 *   four unrouted industries have no `/{kebab}/{zip}` page to collapse onto.
 */
export const resolveDreamSearchIndustryParam = (
  value: string | string[] | undefined
): DreamSearchIndustryRoute | undefined => {
  if (typeof value !== "string") return undefined;
  return DREAM_SEARCH_INDUSTRY_ROUTES.find((industry) => kebabCase(industry) === value);
};

/**
 * The ZIP that DreamSearch will actually search on, given a `[zip]` route
 * segment.
 *
 * The segment supports a city-state-zip alias (`rome-ga-30161`) alongside a bare
 * ZIP, and the search collapses the former to the latter before geocoding — so
 * `/explore/rome-ga-30161` and `/explore/30161` render identical results and
 * must therefore share one `og:url` identity.
 *
 * Shared with `interpretDreamSearchFilters`, which is where this rule is
 * enforced for the query itself. Keeping one implementation is the point: if the
 * identity and the query disagree about which ZIP a URL means, the identity is
 * wrong by construction.
 */
export const normalizeDreamSearchZip = <Input extends string | undefined>(zip: Input): Input =>
  (zip && /(?<=.+-)[0-9]{5}/.test(zip) ? zip.slice(zip.lastIndexOf("-") + 1) : zip) as Input;

/**
 * Whether a value is a well-formed five-digit ZIP.
 *
 * Two callers, both of which need a ZIP they can trust: `pageHelpers` falls back
 * to `FALLBACK_ZIP` when the request's geo yields anything else, and
 * `dreamSearchMetadata` emits an `og:url` only for a segment that passes.
 *
 * The `[zip]` segment is free-form and the page renders 200 for anything truthy
 * (`getGeoCenterFromRequest` short-circuits on any non-empty zip, and the search
 * itself runs client-side), so `/explore/60601-1234` is a soft-404 that looks
 * like a page. Normalization can also *produce* an empty string
 * (`rome-ga-30161-` → `lastIndexOf("-")` is the trailing hyphen → `""`), which
 * would leave the bare redirect-only `/explore` as the derived path.
 *
 * Applied to the *normalized* zip.
 */
export const isWellFormedDreamSearchZip = (zip: string | undefined): zip is string =>
  !!zip && /^[0-9]{5}$/.test(zip);

/** Query ZIPs override the route only when they name one well-formed location. */
export const resolveDreamSearchQueryZip = (
  value: string | string[] | undefined
): string | undefined => {
  if (typeof value !== "string") return undefined;
  const zip = normalizeDreamSearchZip(value);
  return isWellFormedDreamSearchZip(zip) ? zip : undefined;
};
